import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { CountryFilter, countryFilterToCountry, countryFilterToFlagEmoji, countryFilterToName } from "../model/country_filter";
import { Country, countryToFlagEmoji, countryName } from "../model/country";
import { Platform } from "../model/platform";

export type SearchEvent = CustomEvent<{
    term: string;
    countryFilter: CountryFilter;
    platform: Platform;
}>;

export type FilterEvent = CustomEvent<{
    countryFilter: CountryFilter;
    platform: Platform;
}>;

@customElement("searchbar-component")
export class SearchbarComponent extends LitElement {
    @state() private term: string = "";

    @property({ type: String }) placeholder: string = "Add keywords";
    @property({ type: Object }) countryFilter: CountryFilter = CountryFilter.UnitedStates;
    @property({ type: Object }) platform: Platform = Platform.iPhone;

    @property({ type: Boolean }) showAllCountries: boolean = false;
    @property({ type: Boolean }) clearOnSubmit: boolean = false;
    @property({ type: Boolean }) loading: boolean = false;

    createRenderRoot() {
        return this;
    }

    render() {
        return html`
            <div class="flex flex-col md:flex-row space-y-2 md:space-y-0 space-x-0 md:space-x-2">
                <input
                    class="input input-bordered w-full"
                    type="text"
                    placeholder=${this.placeholder}
                    .value=${this.term}
                    @input=${(e: InputEvent) => (this.term = (e.target as HTMLInputElement).value)}
                    @keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.handleSubmit()}
                    ?disabled=${this.loading || this.countryFilter === CountryFilter.All}
                />

                <div class="flex flex-row space-x-2 w-full md:max-w-fit">
                    <select
                        class="select select-bordered w-full md:max-w-fit"
                        .value=${this.countryFilter}
                        @change=${(e: Event) => this.handleCountryChange(e)}
                        ?disabled=${this.loading}
                    >
                        ${Object.values(CountryFilter)
                            .filter((countryFilter) => this.showAllCountries || countryFilter !== CountryFilter.All)
                            .map(
                                (countryFilter) =>
                                    html`<option value=${countryFilter} ?selected=${this.countryFilter === countryFilter}>
                                        ${countryFilterToFlagEmoji(countryFilter)} ${countryFilterToName(countryFilter)}
                                    </option>`
                            )}
                    </select>
                    <select
                        class="select select-bordered w-full md:max-w-fit"
                        .value=${this.platform}
                        @change=${(e: Event) => this.handlePlatformChange(e)}
                        ?disabled=${this.loading}
                    >
                        ${Object.values(Platform).map(
                            (platform) =>
                                html`<option value=${platform} ?selected=${this.platform === platform}>
                                    ${Object.keys(Platform).find((key) => Platform[key as keyof typeof Platform] === platform)}
                                </option>`
                        )}
                    </select>
                </div>
            </div>
        `;
    }

    private handleCountryChange(e: Event) {
        const countryFilter = (e.target as HTMLSelectElement).value as CountryFilter;
        this.dispatchEvent(new CustomEvent("filter", { detail: { countryFilter, platform: this.platform } }));
    }

    private handlePlatformChange(e: Event) {
        const platform = (e.target as HTMLSelectElement).value as Platform;
        this.dispatchEvent(new CustomEvent("filter", { detail: { countryFilter: this.countryFilter, platform } }));
    }

    private handleSubmit() {
        this.term = this.term.trim().toLowerCase();
        const detail = {
            term: this.term,
            countryFilter: this.countryFilter,
            platform: this.platform,
        };
        this.dispatchEvent(new CustomEvent("search", { detail, bubbles: true }));
        if (this.clearOnSubmit) {
            this.term = "";
        }
    }
}
